import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { object as yupObject, string as yupString } from "yup";

import {
  Button,
  CheckboxGroup,
  TelephoneMask,
  TextInput,
  Span,
  Link as DSLink,
} from "@icg360/design-system";

import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { phoneNumberYupString } from "consts";
import { useUser } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import {
  WindDefenseContext,
  type WindDefenseContextState,
} from "pages/offer/wind-defense/wind-defense-flow";
import { trackEvent, useFlags } from "utils";

import { useWindDefenseEnrollmentSteps } from "./hooks";
import styles from "./shingle-reseal.module.scss";
import { WaiverTermsModal } from "./waiver-terms";

const schema = yupObject()
  .shape({
    firstName: yupString().required("Please enter your first name."),
    lastName: yupString().required("Please enter your last name."),
    emailAddress: yupString()
      .required("Please enter an email address.")
      .email("Please enter a valid email address."),
    phone: phoneNumberYupString(),
  })
  .required();

type ContactFormFields = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phone: string;
};

export const ContactInformation = () => {
  const user = useUser();
  const { state, setFieldsAndNavigate, formNavigate } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);
  const [agreedTerms, setAgreedTerms] = useState<string[]>([]);
  const { windDefenseReduceFlow } = useFlags();
  const { stepLabels, totalSteps, step } = useWindDefenseEnrollmentSteps(state);

  const [showSageSureWaiver, setShowSageSureWaiver] = useState(false);
  const [showLiftLockTermsOfService, setShowLiftLockTermsOfService] =
    useState(false);

  const ssWaiverContent = state?.termsSageSure;
  const liftLockTermsContent = state?.termsLiftLock;

  // phone isn't in the default values here
  // but is passed in below because of how the PhoneNumber mask works
  const defaultValues = {
    firstName: state?.firstName ?? user?.firstName ?? "",
    lastName: state?.lastName ?? user?.lastName ?? "",
    emailAddress: state?.emailAddress ?? user?.email ?? "",
    // phone: state?.phone ?? "", // TODO: prepopulated phone number? claims constants???
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ContactFormFields>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  const onSubmit = (data) => {
    if (windDefenseReduceFlow) {
      trackEvent(
        "Wind Defense Reduce Flow: Submit Contact Information Clicked",
        {
          data: JSON.stringify(data),
          agreedToTerms: !!agreedTerms.length,
        },
      );
      setFieldsAndNavigate("../contact-information-confirmation", { ...data });
    } else {
      setFieldsAndNavigate("../waiver-sagesure", { ...data });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <MultiStepFormLayout
          showLogo={!!windDefenseReduceFlow}
          labels={stepLabels}
          step={step}
          totalSteps={totalSteps}
          heading={
            windDefenseReduceFlow
              ? "Confirm your contact details"
              : "How can our partner reach you?"
          }
          subheading={
            !windDefenseReduceFlow
              ? "LiftLock will reach out if your property is selected for Wind Defense."
              : undefined
          }
          buttonProps={{
            primary: {
              type: "submit",
              disabled: windDefenseReduceFlow
                ? !isValid || agreedTerms.length === 0
                : !isValid,
            },
            cancel: { path: "/my/property-services/wind-defense" },
            promptOnCancel: true,
            formNavigate,
          }}
        >
          {!!windDefenseReduceFlow && (
            <div className={styles.confirmationIntro}>
              <Span size="md">
                LiftLock's technicians will reach out to you by phone to
                schedule your Wind Defense service. If you have any questions,
                check out the{" "}
                <DSLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://sagesure.widen.net/s/khnx5fkmrq/liftlock-wind-defense-research-program-faq"
                >
                  Wind Defense FAQs
                </DSLink>
                .
              </Span>
            </div>
          )}
          <div className={styles.contactNameFields}>
            <div className={styles.contactNameField}>
              <TextInput
                label="First name"
                {...register("firstName")}
                errorMessage={errors.firstName?.message}
                isError={!!errors.firstName}
                data-testid="shingle-firstname"
              />
            </div>
            <div className={styles.contactNameField}>
              <TextInput
                label="Last name"
                {...register("lastName")}
                errorMessage={errors.lastName?.message}
                isError={!!errors.lastName}
                data-testid="shingle-lastname"
              />
            </div>
          </div>
          <TextInput
            label="Email"
            {...register("emailAddress")}
            errorMessage={errors.emailAddress?.message}
            isError={!!errors.emailAddress}
            data-testid="shingle-email"
          />
          <TelephoneMask
            label="Phone number"
            {...register("phone")}
            errorMessage={errors.phone?.message}
            isError={!!errors.phone}
            value={state?.phone?.replace(/\D+/g, "") ?? ""}
            data-testid="shingle-phone"
          />
          {!!windDefenseReduceFlow && (
            <div className={styles.confirmationCheckboxWrap}>
              <CheckboxGroup
                value={agreedTerms}
                onChange={(val) => setAgreedTerms(val)}
                aria-label="Consent to have read and agreed to the SageSure Waiver and LiftLock's Terms of Service"
              >
                <CheckboxGroup.Checkbox multiline value="agreedTerms">
                  I have read and agreed to the{" "}
                  <Button
                    className={styles.wdTermsLink}
                    onPress={() => {
                      setShowSageSureWaiver(true);
                    }}
                    appearance="link"
                    size="default"
                  >
                    SageSure Waiver
                  </Button>{" "}
                  and{" "}
                  <Button
                    className={styles.wdTermsLink}
                    onPress={() => {
                      setShowLiftLockTermsOfService(true);
                    }}
                    appearance="link"
                    size="default"
                  >
                    LiftLock's Terms of Service
                  </Button>
                  .
                </CheckboxGroup.Checkbox>
              </CheckboxGroup>
            </div>
          )}
        </MultiStepFormLayout>
      </form>
      <WaiverTermsModal
        header="SageSure Waiver"
        content={ssWaiverContent}
        show={showSageSureWaiver}
        onClose={() => setShowSageSureWaiver(false)}
      />
      <WaiverTermsModal
        header="LiftLock's Terms of Service"
        content={liftLockTermsContent}
        show={showLiftLockTermsOfService}
        onClose={() => setShowLiftLockTermsOfService(false)}
      />
    </>
  );
};
